import {Flyout} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/navigation_bar.types';
import {LinkContainer, mediaDesktopAndUp} from '@aboutyou/ui-components';
import React from 'react';
import {styled} from '@linaria/react';
import {useTrackFlyoutOriginClick} from '../../hooks/useOriginClickTrackingFunction';
import {ImageSectionsContainer} from './ImageContainer/ImageSectionsContainer';
import {LogoContainer} from './LogoContainer/LogoContainer';
import {useIsFeatureActive} from '@aboutyou/feature-flags';

interface Props {
  flyout?: Flyout;
  shouldApplyRatioOnContentBlockImageTile?: boolean;
  isActive: boolean;
  testId: string;
}

export const NavigationFlyoutContent: React.FC<Props> = ({
  flyout,
  isActive,
  testId,
}) => {
  const trackCategoryClick = useTrackFlyoutOriginClick();
  const {isActive: isSaleIndicationActive} = useIsFeatureActive(
    'desktop_nav_sale_indications',
  );

  if (!flyout) {
    return null;
  }

  const hasLinkContainers = flyout.containers.some(
    container => container.type?.$case === 'linkContainer',
  );

  const logosContainer = flyout.containers.find(
    container => container.type?.$case === 'logoContainer',
  );
  const wrapItemsOnTablet =
    // Wrap the logo + images container into multiple rows on tablet if there are multiple logos-section. Example: Brands flyout
    (logosContainer?.type?.$case === 'logoContainer' &&
      logosContainer.type.logoContainer.sections.length >= 3) ||
    // Wrap the images container into multiple rows on tablet if there are multiple imageContainers. Example: Drops flyout
    flyout.containers.filter(
      container => container.type?.$case === 'imageContainer',
    ).length === 4;

  return (
    <ContentWrapper data-active={isActive} data-testid={testId}>
      {hasLinkContainers && (
        <>
          {flyout.containers.map((container, index) => {
            switch (container.type?.$case) {
              case 'linkContainer': {
                return (
                  <LinkContainer
                    key={`list_section_container_${index}`}
                    dataTestId={`navigationFlyoutContentLinkContainer-${index}`}
                    sections={container.type.linkContainer.sections}
                    onLinkItemClick={trackCategoryClick}
                    isSaleIndicationActive={isSaleIndicationActive}
                  />
                );
              }
              default:
                return null;
            }
          })}
        </>
      )}

      <WrappableBox wrapItemsOnTablet={wrapItemsOnTablet}>
        {flyout.containers.map((container, index) => {
          switch (container.type?.$case) {
            case 'imageContainer': {
              return (
                <ImageSectionsContainer
                  key={`image_section_container_${index}`}
                  sections={container.type.imageContainer.sections}
                />
              );
            }
            case 'logoContainer': {
              return (
                <LogoContainer
                  key={`logo_section_container_${index}`}
                  dataTestId={`navigationFlyoutContentLogoContainer-${index}`}
                  sections={container.type.logoContainer.sections}
                />
              );
            }
            default:
              return null;
          }
        })}
      </WrappableBox>
    </ContentWrapper>
  );
};

const ContentWrapper = styled.div`
  display: none;
  flex-direction: row;
  gap: 40px;
  width: 100%;
  flex-wrap: wrap;

  @media ${mediaDesktopAndUp} {
    height: 100%;
    flex-wrap: nowrap;
  }

  &[data-active='true'] {
    display: flex;
  }
`;

const WrappableBox = styled.div<{wrapItemsOnTablet?: boolean}>`
  display: flex;
  gap: 40px 20px;

  flex-wrap: ${({wrapItemsOnTablet}) =>
    wrapItemsOnTablet ? 'wrap' : 'nowrap'};

  @media ${mediaDesktopAndUp} {
    flex-wrap: nowrap;
  }
`;
